// Locations.tsx
import { useEffect, useState } from "react";
import {
  Box,
  Pagination,
  Stack,
  CircularProgress,
  Typography,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import { useFetchContext } from "../../../contexts/FetchContext";
import { IDataPagination } from "../../../interfaces";
import { PageContainer } from "../../shared/components/PageContainer";
import NoResult from "../../shared/components/NoResult";
import TableFilter from "../../shared/components/TableFilter";
import LocationsTable from "./LocationsTable";
import { WarehouseLocation } from "../../../interfaces/document";
import LocationForm from "./LocationForm";
import ConfirmationModal from "../../shared/components/Modal/ConfirmationModal";
import SwipingModal from "../../shared/components/SwipingModal";
import { useAuthContext } from "../../../contexts/AuthContext";
import { AxiosError } from "axios";
import { toast } from "react-toastify";

export const Locations = () => {
  const { state, fetchData } = useFetchContext();

  const { userData } = useAuthContext();
  const [order, setOrder] = useState<"ASC" | "DESC">("ASC");
  const [orderType, setOrderType] = useState<string>("name");
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>(
    undefined
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] =
    useState<WarehouseLocation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSortChange = (state: "ASC" | "DESC") => {
    setOrder(state);
  };

  const handleOrderTypeChange = (type: string) => {
    setOrderType(type);
  };

  const handleSearchChange = (searchTerm: string) => {
    setSearchKeyword(searchTerm);
    setPage(1);
  };

  const handleDeleteClick = (location: WarehouseLocation) => {
    setSelectedLocation(location);
    setDeleteModalOpen(true);
  };

  const handleEditClick = (location: WarehouseLocation) => {
    setSelectedLocation(location);
    setIsFormModalOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedLocation(null);
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = async (name: string) => {
    setIsSubmitting(true);
    try {
      if (selectedLocation) {
        // Edit
        await fetchData(
          `/warehouse-locations/${selectedLocation.uuid}`,
          "PUT",
          {
            name,
          }
        );

        toast.success("Ubicazione aggiornata con successo");
      } else {
        // Create
        await fetchData("/warehouse-locations", "POST", {
          name: name,
          warehouseId: userData?.user?.warehouseId,
        });

        toast.success("Ubicazione creata con successo");
      }
      setIsFormModalOpen(false);
      fetchDataAsync();
    } catch (error) {
      console.error("Error saving location:", error);
    }
    setIsSubmitting(false);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLocation) return;

    setIsDeleting(true);
    try {
      await fetchData(
        `/warehouse-locations/${selectedLocation.uuid}`,
        "DELETE"
      );

      toast.success("Ubicazione rimossa con successo");

      fetchDataAsync();
    } catch (error) {
      const axiosError = error as AxiosError;
      toast.error(
        axiosError?.message ?? "Errore con la comunicazione con il server"
      );
    }
    setIsDeleting(false);
    setDeleteModalOpen(false);
    setSelectedLocation(null);
  };

  const [data, setData] = useState<IDataPagination<WarehouseLocation> | null>(
    null
  );
  const [page, setPage] = useState(1);

  const fetchDataAsync = async () => {
    try {
      const response = await fetchData<IDataPagination<WarehouseLocation>>(
        `/warehouse-locations?warehouseId=${
          userData?.user?.warehouseId
        }&pageSize=6&page=${page}&${
          searchKeyword ? `searchKeyword=${searchKeyword}` : ""
        }&orderType=${order}&orderBy=${orderType}`
      );

      setData(response?.data);
    } catch (error) {}
  };

  useEffect(() => {
    fetchDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchKeyword, order, orderType]);

  return (
    <PageContainer>
      <Stack>
        <Typography variant="h6" mb={3} mt={1} align="center">
          TOTALE UBICAZIONI: {data?.total ?? 0}
        </Typography>
        <Grid container>
          <Grid item xs={10} sx={{ pb: 2 }}>
            <TableFilter
              sortOrder={order}
              onSearchChange={handleSearchChange}
              searchKeyword={searchKeyword}
            />
          </Grid>
          <Grid
            item
            xs={2}
            sx={{ pb: 2, display: "flex", justifyContent: "center" }}
          >
            <Button color="primary" onClick={handleCreateClick}>
              <AddIcon />
            </Button>
          </Grid>

          {state.isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "80vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : data?.total ? (
            <LocationsTable
              data={data?.items ?? []}
              order={order}
              orderType={orderType}
              handeSortChange={handleSortChange}
              handleOrderTypeChange={handleOrderTypeChange}
              onDeleteClick={handleDeleteClick}
              onEditClick={handleEditClick}
            />
          ) : (
            <NoResult />
          )}
        </Grid>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pb: 15,
            mt: 5,
          }}
        >
          <Pagination
            count={data?.totalPages ?? 0}
            size="small"
            onChange={(e, page) => setPage(page)}
            page={page}
          />
        </Box>
      </Stack>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        title="Conferma Eliminazione"
        text={`Sei sicuro di voler eliminare l'ubicazione "${selectedLocation?.name}"?`}
      />

      <SwipingModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        title={selectedLocation ? "Modifica Ubicazione" : "Nuova Ubicazione"}
        content={
          <LocationForm
            initialData={selectedLocation}
            onSubmit={handleFormSubmit}
            isLoading={isSubmitting}
          />
        }
      />
    </PageContainer>
  );
};

export default Locations;
